/**
 * Lohnbuchungsbeleg Schweiz — API client (thin wrapper over fetchApi, which
 * unwraps the {success,data} envelope). Binary exports use a raw fetch + blob
 * download like the AR module.
 */

import { fetchApi } from './api';
import type {
    RunSummary, RunDetail, PreviewResult, TemplateResponse, RohField, BcExportConfig,
} from '../types/lohnBuchungsbeleg';

const BASE_URL = '/api/v1';

async function downloadFile(url: string, fallbackName: string): Promise<void> {
    const res = await fetch(`${BASE_URL}${url}`);
    if (res.status === 401) {
        const { redirectToLogin } = await import('../utils/auth-redirect');
        redirectToLogin();
        throw new Error('Session expired');
    }
    if (!res.ok) {
        let message = `Download failed (status ${res.status})`;
        try {
            const body = await res.json() as { error?: { message?: string } };
            if (body.error?.message) message = body.error.message;
        } catch { /* non-JSON */ }
        throw new Error(message);
    }
    const disposition = res.headers.get('content-disposition') || '';
    const match = /filename="?([^"]+)"?/.exec(disposition);
    const filename = match?.[1] || fallbackName;
    const blob = await res.blob();
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(objectUrl);
}

export interface ColumnMapping {
    sheetName?: string;
    headerRow?: number;
    columns?: Partial<Record<RohField, string>>;
}

export const lohnBuchungsbelegApi = {
    // ---- Runs ----
    listRuns: () =>
        fetchApi<{ runs: RunSummary[] }>('/lohn-buchungsbeleg/runs').then(r => r.runs),

    getRun: (id: number) =>
        fetchApi<RunDetail>(`/lohn-buchungsbeleg/runs/${id}`),

    preview: (fileBase64: string, fileName: string, period: string, mapping?: ColumnMapping, password?: string) =>
        fetchApi<PreviewResult>('/lohn-buchungsbeleg/preview', {
            method: 'POST',
            body: JSON.stringify({ fileBase64, fileName, period, mapping, password }),
        }),

    createRun: (fileBase64: string, fileName: string, period: string, opts?: { mapping?: ColumnMapping; valutaDate?: string | null; force?: boolean; password?: string }) =>
        fetchApi<{ runId: number; period: string; rawRowCount: number; replaced: boolean }>(
            `/lohn-buchungsbeleg/runs${opts?.force ? '?force=true' : ''}`, {
                method: 'POST',
                body: JSON.stringify({ fileBase64, fileName, period, mapping: opts?.mapping, valutaDate: opts?.valutaDate, password: opts?.password }),
            }),

    finalizeRun: (id: number) =>
        fetchApi<{ id: number; status: string; checksum: number; balanced: boolean }>(
            `/lohn-buchungsbeleg/runs/${id}/finalize`, { method: 'POST' }),

    reopenRun: (id: number) =>
        fetchApi<{ id: number; status: string }>(`/lohn-buchungsbeleg/runs/${id}/reopen`, { method: 'POST' }),

    deleteRun: (id: number) =>
        fetchApi<{ id: number; deleted: boolean }>(`/lohn-buchungsbeleg/runs/${id}`, { method: 'DELETE' }),

    // ---- Overrides ----
    setOverride: (runId: number, templateLineId: number, costCenterCode: string, value: number, comment: string) =>
        fetchApi<{ overrideId: number }>(`/lohn-buchungsbeleg/runs/${runId}/overrides`, {
            method: 'POST',
            body: JSON.stringify({ templateLineId, costCenterCode, value, comment }),
        }),

    deleteOverride: (runId: number, templateLineId: number, costCenterCode: string) =>
        fetchApi<{ reverted: boolean }>(
            `/lohn-buchungsbeleg/runs/${runId}/overrides?templateLineId=${templateLineId}&costCenterCode=${encodeURIComponent(costCenterCode)}`,
            { method: 'DELETE' }),

    // ---- Downloads ----
    downloadExport: (runId: number, format: 'xlsx' | 'pdf', period: string) =>
        downloadFile(`/lohn-buchungsbeleg/runs/${runId}/export?format=${format}`, `Buchungsbeleg_${period}.${format}`),

    /** Download the derived booking entries (Buchungssätze) as Excel/PDF. */
    downloadJournalExport: (runId: number, format: 'xlsx' | 'pdf', period: string) =>
        downloadFile(`/lohn-buchungsbeleg/runs/${runId}/export?format=${format}&view=journal`, `Buchungssaetze_${period}.${format}`),

    // ---- Template (Settings) ----
    getTemplate: () =>
        fetchApi<TemplateResponse>('/lohn-buchungsbeleg/template'),

    createLine: (body: Record<string, unknown>) =>
        fetchApi<{ id: number }>('/lohn-buchungsbeleg/template/lines', { method: 'POST', body: JSON.stringify(body) }),

    updateLine: (id: number, body: Record<string, unknown>) =>
        fetchApi<{ id: number }>(`/lohn-buchungsbeleg/template/lines/${id}`, { method: 'PUT', body: JSON.stringify(body) }),

    deleteLine: (id: number) =>
        fetchApi<{ id: number }>(`/lohn-buchungsbeleg/template/lines/${id}`, { method: 'DELETE' }),

    /** Persist a new template-line order (top-to-bottom ids); reassigns SortOrder 10,20,30,… */
    reorderLines: (ids: number[]) =>
        fetchApi<{ reordered: number }>('/lohn-buchungsbeleg/template/lines/reorder', {
            method: 'PUT', body: JSON.stringify({ ids }),
        }),

    createCostCenter: (body: Record<string, unknown>) =>
        fetchApi<{ id: number }>('/lohn-buchungsbeleg/template/cost-centers', { method: 'POST', body: JSON.stringify(body) }),

    updateCostCenter: (id: number, body: Record<string, unknown>) =>
        fetchApi<{ id: number }>(`/lohn-buchungsbeleg/template/cost-centers/${id}`, { method: 'PUT', body: JSON.stringify(body) }),

    deleteCostCenter: (id: number) =>
        fetchApi<{ id: number }>(`/lohn-buchungsbeleg/template/cost-centers/${id}`, { method: 'DELETE' }),

    /** Persist a new cost-center order (left-to-right ids); reassigns SortOrder 10,20,30,… */
    reorderCostCenters: (ids: number[]) =>
        fetchApi<{ reordered: number }>('/lohn-buchungsbeleg/template/cost-centers/reorder', {
            method: 'PUT', body: JSON.stringify({ ids }),
        }),

    // ---- Per-account MWST posting groups (BC export) ----
    createVatAccount: (body: Record<string, unknown>) =>
        fetchApi<{ id: number }>('/lohn-buchungsbeleg/template/vat-accounts', { method: 'POST', body: JSON.stringify(body) }),

    updateVatAccount: (id: number, body: Record<string, unknown>) =>
        fetchApi<{ id: number }>(`/lohn-buchungsbeleg/template/vat-accounts/${id}`, { method: 'PUT', body: JSON.stringify(body) }),

    deleteVatAccount: (id: number) =>
        fetchApi<{ id: number }>(`/lohn-buchungsbeleg/template/vat-accounts/${id}`, { method: 'DELETE' }),

    // ---- BC clipboard export layout ----
    /**
     * The configurable BC column layout (or the default 21-column layout if none saved yet).
     * Pass { default: true } to always get the built-in default (for "reset to default").
     */
    getBcExportLayout: (opts?: { default?: boolean }) =>
        fetchApi<BcExportConfig>(`/lohn-buchungsbeleg/bc-export-layout${opts?.default ? '?default=1' : ''}`),

    /** Persist the whole BC column layout config. */
    saveBcExportLayout: (config: BcExportConfig) =>
        fetchApi<{ saved: boolean }>('/lohn-buchungsbeleg/bc-export-layout', {
            method: 'PUT', body: JSON.stringify(config),
        }),
};

/** Read a File into a base64 string (without the data: prefix). */
export function fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve((reader.result as string).split(',')[1] ?? '');
        reader.onerror = () => reject(new Error('Could not read file'));
        reader.readAsDataURL(file);
    });
}

/** Format a money value for display: blank for zero, Swiss thousands otherwise. */
export function fmtMoney(n: number, blankZero = true): string {
    if (blankZero && Math.abs(n) < 0.005) return '';
    const neg = n < 0;
    const [int, dec] = Math.abs(n).toFixed(2).split('.');
    const grouped = int.replace(/\B(?=(\d{3})+(?!\d))/g, "'");
    return `${neg ? '-' : ''}${grouped}.${dec}`;
}
